<template>
  <div
    class="objects-table-theme"
    :class="getBaseTypeThemeClass(resolvedBaseType)"
    :style="getBaseTypeThemeStyle(resolvedBaseType)"
  >
    <div v-if="loading" class="list-group">
      <div class="list-group-item py-4 text-muted">Einträge werden geladen...</div>
    </div>

    <div v-else-if="!paginatedObjects.length" class="list-group">
      <div class="list-group-item py-4 text-muted">Keine Einträge vorhanden.</div>
    </div>

    <div v-else class="objects-list-wrap">
      <div class="objects-list-toolbar">
        <label class="objects-list-toolbar__label" for="objects-sort">Sortierung</label>
        <select id="objects-sort" v-model="sortKey" class="form-select form-select-sm objects-list-toolbar__select">
          <option value="list_position">Position</option>
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
          <option value="enabled_desc">Veröffentlichte zuerst</option>
          <option value="enabled_asc">Entwürfe zuerst</option>
          <option v-if="showDates" value="date_asc">Startdatum (aufsteigend)</option>
          <option v-if="showDates" value="date_desc">Startdatum (absteigend)</option>
        </select>
      </div>

      <div class="list-group objects-list">
      <Sortable
        v-if="sortableEnabled"
        :list="sortablePageObjects"
        item-key="id"
        tag="div"
        class="list-group objects-list"
        :options="{animation: 150, handle: '.objects-list__drag'}"
        @end="onListSortEnd"
      >
        <template #item="{element, index}">
          <article class="list-group-item objects-list__item">
            <div class="objects-list__main">
              <div class="objects-table-name">
                <span class="objects-list__drag" title="Sortieren">
                  <Icon icon="fa-pro-solid:grip-vertical" />
                </span>
                <strong>{{ element.name }}</strong>
              </div>

              <div class="objects-list__meta">
                <span class="badge text-bg-light objects-list__position-badge"># {{ element.list_position || (currentPageOffset + index + 1) }}</span>
                <ObjectNeedsReviewIcon v-if="element.needs_review" :label="reviewLabel" />
                <span class="objects-list__status" :class="statusBadgeClass(element.enabled)">
                  <Icon :icon="statusBadgeIcon(element.enabled)" />
                  {{ statusBadgeText(element.enabled, enabledStatusActiveText, enabledStatusInactiveText) }}
                </span>
                <span
                  v-if="showMarketingPortalColumn"
                  class="objects-list__status"
                  :class="statusBadgeClass(element.setting_enable_in_manet_stadt_abl)"
                >
                  <Icon :icon="statusBadgeIcon(element.setting_enable_in_manet_stadt_abl)" />
                  {{ statusBadgeText(element.setting_enable_in_manet_stadt_abl, 'Online auf mvp.de', 'Nicht auf mvp.de') }}
                </span>
                <span
                  v-if="showDates && element.start_on && element.end_on"
                  class="objects-list__meta-item objects-list__meta-item--newline"
                  :class="{'text-danger': isPastEndOn(element.end_on)}"
                >
                  <span class="objects-list__meta-label">Zeitraum:</span>
                  {{ formatDate(element.start_on, 'short2') }} - {{ formatDate(element.end_on, 'short2') }}
                </span>
              </div>
            </div>

            <div class="objects-list__actions">
              <template v-for="lang in element.locales" :key="`${element.id}-${lang}`">
                <router-link
                  class="me-2 d-inline-flex align-items-center"
                  :title="`${lang.toUpperCase()} bearbeiten`"
                  :to="{name: `${routePrefix}.edit`, params: {id: element.id, lang}}"
                >
                  <ActionIcon :icon="'flagpack:'+ (lang === 'en' ? 'gb-nir' : lang)" overlay-icon="fa-pro-solid:pencil" />
                </router-link>
              </template>
              <router-link
                v-if="showStatsAction"
                class="me-2 d-inline-flex align-items-center"
                title="Statistik"
                :to="{name: `${routePrefix}.stats`, params: {id: element.id}}"
              >
                <ActionIcon icon="fa-pro-solid:chart-pie" />
              </router-link>
            </div>
          </article>
        </template>
      </Sortable>
      <template v-else>
        <article v-for="data in paginatedObjects" :key="data.id" class="list-group-item objects-list__item">
          <div class="objects-list__main">
            <div class="objects-table-name">
              <strong>{{ data.name }}</strong>
            </div>

            <div class="objects-list__meta">
              <ObjectNeedsReviewIcon v-if="data.needs_review" :label="reviewLabel" />
              <span class="objects-list__status" :class="statusBadgeClass(data.enabled)">
                <Icon :icon="statusBadgeIcon(data.enabled)" />
                {{ statusBadgeText(data.enabled, enabledStatusActiveText, enabledStatusInactiveText) }}
              </span>
              <span
                v-if="showMarketingPortalColumn"
                class="objects-list__status"
                :class="statusBadgeClass(data.setting_enable_in_manet_stadt_abl)"
              >
                <Icon :icon="statusBadgeIcon(data.setting_enable_in_manet_stadt_abl)" />
                {{ statusBadgeText(data.setting_enable_in_manet_stadt_abl, 'Online auf mvp.de', 'Nicht auf mvp.de') }}
              </span>
              <span
                v-if="showDates && data.start_on && data.end_on"
                class="objects-list__meta-item objects-list__meta-item--newline"
                :class="{'text-danger': isPastEndOn(data.end_on)}"
              >
                <span class="objects-list__meta-label">Zeitraum:</span>
                {{ formatDate(data.start_on, 'short2') }} - {{ formatDate(data.end_on, 'short2') }}
              </span>
            </div>
          </div>

          <div class="objects-list__actions">
            <template v-for="lang in data.locales" :key="`${data.id}-${lang}`">
              <router-link
                class="me-2 d-inline-flex align-items-center"
                :title="`${lang.toUpperCase()} bearbeiten`"
                :to="{name: `${routePrefix}.edit`, params: {id: data.id, lang}}"
              >
                <ActionIcon :icon="'flagpack:'+ (lang === 'en' ? 'gb-nir' : lang)" overlay-icon="fa-pro-solid:pencil" />
              </router-link>
            </template>
            <router-link
              v-if="showStatsAction"
              class="me-2 d-inline-flex align-items-center"
              title="Statistik"
              :to="{name: `${routePrefix}.stats`, params: {id: data.id}}"
            >
              <ActionIcon icon="fa-pro-solid:chart-pie" />
            </router-link>
          </div>
        </article>
      </template>
      </div>
    </div>

    <nav v-if="showPagination" :aria-label="paginationAriaLabel" class="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
      <small class="text-muted">Zeige {{ currentPageOffset + 1 }} bis {{ currentPageOffset + paginatedObjects.length }} von {{ resolvedTotalRecords }}</small>
      <ul class="pagination pagination-sm mb-0">
        <li class="page-item" :class="{disabled: currentPage <= 1}">
          <button class="page-link" type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1">Zurück</button>
        </li>
        <li
          v-for="page in visiblePages"
          :key="`page-${page}`"
          class="page-item"
          :class="{active: page === currentPage}"
        >
          <button class="page-link" type="button" @click="goToPage(page)">{{ page }}</button>
        </li>
        <li class="page-item" :class="{disabled: currentPage >= totalPages}">
          <button class="page-link" type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">Weiter</button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>

import {useObjectStore} from "@/stores/object";
import ActionIcon from "@/components/general/ActionIcon.vue";
import ObjectNeedsReviewIcon from "@/components/objects/ObjectNeedsReviewIcon.vue";
import {OBJECT_REVIEW_LABEL} from "@/utils/objectReview";
import {getBaseTypeTheme, getBaseTypeThemeClass, getBaseTypeThemeStyle, resolveBaseTypeKey} from "@/utils/baseTypeTheme";
import {Sortable} from "sortablejs-vue3";

export default {
  emits: ['page'],
  setup() {
    const objectStore = useObjectStore()
    return {
      objectStore,
    }
  },
  data() {
    return {
      tableObjects: [...this.objects],
      currentPage: 1,
      sortKey: 'list_position',
    }
  },
  props: {
    objects: {
      default: () => [],
      type: Array
    },
    showDates: {
      default: false,
      type: Boolean,
    },
    enableSortable: {
      default: false,
      type: Boolean,
    },
    rows: {
      default: 10,
      type: Number,
    },
    totalRecords: {
      default: null,
      type: Number,
    },
    loading: {
      default: false,
      type: Boolean,
    },
    lazy: {
      default: false,
      type: Boolean,
    },
    showStatsAction: {
      default: false,
      type: Boolean,
    },
    baseType: {
      default: null,
      type: String,
    },
    hasClientWebsite: {
      default: false,
      type: Boolean,
    }
  },
  components: {
    Sortable,
    ActionIcon,
    ObjectNeedsReviewIcon,
  },
  watch: {
    objects(newValue) {
      this.tableObjects = [...newValue];
    },
    rows() {
      this.currentPage = 1
    }
  },
  computed: {
    reviewLabel() {
      return OBJECT_REVIEW_LABEL
    },
    routePrefix() {
      return this.resolvedBaseType
    },
    resolvedTotalRecords() {
      return this.totalRecords ?? this.tableObjects.length
    },
    resolvedBaseType() {
      return resolveBaseTypeKey(this.baseType || this.$route.name || this.$route.meta?.base_type)
    },
    paginationAriaLabel() {
      return `${getBaseTypeTheme(this.resolvedBaseType).label}-Seiten`
    },
    pageSize() {
      return Number(this.rows || 10)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.resolvedTotalRecords / this.pageSize))
    },
    showPagination() {
      return this.resolvedTotalRecords > 10
    },
    currentPageOffset() {
      return (this.currentPage - 1) * this.pageSize
    },
    paginatedObjects() {
      const sorted = [...this.tableObjects].sort(this.compareObjects)

      if (this.lazy) {
        return sorted
      }

      return sorted.slice(this.currentPageOffset, this.currentPageOffset + this.pageSize)
    },
    sortablePageObjects: {
      get() {
        return this.paginatedObjects
      },
      set(newValue) {
        if (this.lazy) {
          this.tableObjects = [...newValue]
          return
        }

        const start = this.currentPageOffset
        this.tableObjects.splice(start, newValue.length, ...newValue)
      },
    },
    visiblePages() {
      const windowSize = 5
      const start = Math.max(1, this.currentPage - Math.floor(windowSize / 2))
      const end = Math.min(this.totalPages, start + windowSize - 1)
      const adjustedStart = Math.max(1, end - windowSize + 1)
      const pages = []
      for (let i = adjustedStart; i <= end; i += 1) {
        pages.push(i)
      }
      return pages
    },
    sortableEnabled() {
      if (!this.enableSortable) {
        return false;
      }
      if (this.sortKey !== 'list_position') {
        return false;
      }
      return true;
    },
    enabledStatusActiveText() {
      return this.showMarketingPortalColumn ? 'Online auf Ihrer Website' : 'Online auf mvp.de'
    },
    enabledStatusInactiveText() {
      return 'Entwurf – nicht öffentlich'
    },
    showMarketingPortalColumn() {
      return this.resolvedBaseType === 'offers' && this.hasClientWebsite
    }
  },
  methods: {
    isPastEndOn(endOn) {
      if (!endOn) return false;
      const endDate = new Date(endOn);
      if (Number.isNaN(endDate.getTime())) return false;
      endDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate < today;
    },
    getBaseTypeThemeClass,
    getBaseTypeThemeStyle,
    statusBadgeClass(value) {
      return value ? 'objects-list__status--active' : 'objects-list__status--inactive'
    },
    statusBadgeIcon(value) {
      return value ? 'fa-pro-solid:circle-check' : 'fa-pro-solid:circle-xmark'
    },
    statusBadgeText(value, activeText, inactiveText) {
      return value ? activeText : inactiveText
    },
    compareObjects(a, b) {
      if (this.sortKey === 'name_asc') {
        return String(a?.name || '').localeCompare(String(b?.name || ''), 'de')
      }
      if (this.sortKey === 'name_desc') {
        return String(b?.name || '').localeCompare(String(a?.name || ''), 'de')
      }
      if (this.sortKey === 'enabled_desc') {
        return Number(Boolean(b?.enabled)) - Number(Boolean(a?.enabled))
      }
      if (this.sortKey === 'enabled_asc') {
        return Number(Boolean(a?.enabled)) - Number(Boolean(b?.enabled))
      }
      if (this.sortKey === 'date_asc') {
        return new Date(a?.start_on || 0).getTime() - new Date(b?.start_on || 0).getTime()
      }
      if (this.sortKey === 'date_desc') {
        return new Date(b?.start_on || 0).getTime() - new Date(a?.start_on || 0).getTime()
      }

      const aPosition = Number(a?.list_position || 0)
      const bPosition = Number(b?.list_position || 0)
      if (aPosition !== bPosition) {
        return aPosition - bPosition
      }
      return Number(a?.id || 0) - Number(b?.id || 0)
    },
    goToPage(page) {
      const targetPage = Math.min(Math.max(page, 1), this.totalPages)
      if (targetPage === this.currentPage) {
        return
      }

      this.currentPage = targetPage
      if (this.lazy) {
        this.$emit('page', {
          page: targetPage - 1,
          first: (targetPage - 1) * this.pageSize,
          rows: this.pageSize,
        })
      }
    },
    onRowReorder(event) {
      if (!this.sortableEnabled) {
        return;
      }
      const oldOrder = this.tableObjects.map((object) => object.id).join(',');
      this.tableObjects = event.value.map((object, index) => ({
        ...object,
        list_position: index + 1,
      }));
      const ids = this.tableObjects.map((object) => object.id);
      if (ids.join(',') === oldOrder) {
        return;
      }
      this.objectStore.apiSort(ids).catch((error) => {
        console.error(error);
      });
    },
    onListSortEnd() {
      if (!this.sortableEnabled) {
        return
      }

      this.tableObjects = this.tableObjects.map((object, index) => ({
        ...object,
        list_position: index + 1,
      }))
      const ids = this.tableObjects.map((object) => object.id)
      this.objectStore.apiSort(ids).catch((error) => {
        console.error(error)
      })
    }
  },
}
</script>

<style scoped lang="scss">
.objects-table-theme {
  padding-top: 0.35rem;
  border-top: 0.35rem solid var(--base-type-primary);
  background: #ffffff;
}

.objects-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem;
  margin-bottom: 0.55rem;
}

.objects-list-toolbar__label {
  margin: 0;
  color: #4e6477;
  font-size: 0.85rem;
  font-weight: 600;
}

.objects-list-toolbar__select {
  width: auto;
  min-width: 13.5rem;
  border-radius: 0;
}

.objects-table-name {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--base-type-text);
  font-weight: 650;
}

.objects-list__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-radius: 0 !important;
}

:deep(.list-group-item) {
  border-radius: 0 !important;
}

.objects-list__main {
  min-width: 0;
  flex: 1;
}

.objects-list__meta {
  margin-top: 0.45rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
}

.objects-list__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #4e6477;
}

.objects-list__meta-item--newline {
  flex-basis: 100%;
}

.objects-list__status {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  min-height: 2rem;
  padding: 0.34rem 0.72rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 750;
  line-height: 1;
  white-space: nowrap;
}

.objects-list__status--active {
  color: #0f5132;
  background: #d1e7dd;
  border-color: #9fd2ba;
}

// Same grey as the draft status in the edit toolbar (.base-type-edit__status-badge--draft).
.objects-list__status--inactive {
  color: #5d6b78;
  background: #eef1f4;
  border-color: #d5dbe1;
}

.objects-list__position-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.objects-list__position-badge:hover {
  cursor: default;
}

.objects-list__meta-label {
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7f90;
}

.objects-list__actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.objects-list__drag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #70879b;
  cursor: grab;
}
</style>
